import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useAuth } from "../useAuth";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import axiosInstance from "@/lib/axios";

type CurrencyExchangeRates = {
  base: string;
  rates: {
    [currencyCode: string]: number;
  };
};

async function fetchCurrencyRates(): Promise<CurrencyExchangeRates> {
  try {
    const response = await axiosInstance.post(
      `${process.env.NEXT_PUBLIC_BACKEND_BASE_URL}/currency/list`,
      {
        bcode: "EUR",
      }
    );

    if (response.status !== 200) {
      throw new Error(response.data.error);
    }
    if (response.data.error) {
      throw new Error(response.data.error);
    }
    return response?.data?.result
      ? response.data.result
      : { base: "EUR", rates: {} };
  } catch (err: any) {
    if (
      err &&
      typeof err === "object" &&
      "isAxiosError" in err &&
      err.isAxiosError
    ) {
      if (!err.response) {
        throw err;
      }
      const axiosError = err as AxiosError;
      const errorMessage = axiosError.response?.data as {
        error: string;
        success: boolean;
      };
      throw new Error(errorMessage?.error ?? axiosError?.message);
    }

    throw new Error("Unknown error occurred! Please refresh the page.");
  }
}

export type UseAttributionDataOptions = {
  enabled?: boolean;
  disableRefetch?: boolean;
  onSuccess?: ((data: CurrencyExchangeRates) => void) | undefined;
};

export function useCurrencyRates(options?: UseAttributionDataOptions) {
  const { data } = useAuth();
  return useQuery({
    queryKey: ["currencyRates"],
    queryFn: () => {
      return fetchCurrencyRates();
    },
    ...options,
    refetchOnWindowFocus: false,
    staleTime: 30000, // 30 seconds needed to overwrite global settings
    gcTime: 30000, // 30 seconds
    enabled:
      options?.enabled !== undefined
        ? options.enabled && !!data?.token
        : !!data?.token,
  });
}
